import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { recordLogin } from "@/api/admin";
import { restMaybeSingle } from "@/lib/http";
import { supabase } from "@/lib/supabase";
import type { Profile } from "@/types/domain";

/**
 * Oturum durumu.
 *
 * `inactive`, `signedIn`den ayrı bir durum: pasife alınan kişinin jetonu süresi
 * dolana kadar geçerli kalıyor ve RLS ilkeleri kendi profilini görmesine izin
 * veriyor. Bu ayrım olmasaydı, pasif kullanıcı boş bir uygulamaya girip her
 * ekranda "yetkiniz yok" görürdü; şimdi giriş ekranında ne olduğunu okuyor.
 */
export type AuthStatus = "loading" | "signedOut" | "signedIn" | "inactive";

export interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

async function fetchProfile(userId: string): Promise<Profile | null> {
  return restMaybeSingle<Profile>("profiles", { select: "*", id: `eq.${userId}` });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const [status, setStatus] = React.useState<AuthStatus>("loading");
  const [session, setSession] = React.useState<Session | null>(null);
  const [profile, setProfile] = React.useState<Profile | null>(null);

  /** Oturuma karşılık gelen profili yükleyip durumu belirler. */
  const applySession = React.useCallback(async (next: Session | null) => {
    setSession(next);

    if (!next) {
      setProfile(null);
      setStatus("signedOut");
      return;
    }

    try {
      const loaded = await fetchProfile(next.user.id);

      setProfile(loaded);
      setStatus(loaded?.is_active ? "signedIn" : "inactive");
    } catch {
      // Profil okunamadıysa oturumu açık saymıyoruz: yetkisi belirsiz bir
      // kullanıcıyı içeri almaktansa giriş ekranında tutmak doğru.
      setProfile(null);
      setStatus("signedOut");
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) void applySession(data.session);
    });

    // Jeton yenilendiğinde, başka bir sekmede çıkış yapıldığında ve parola
    // değiştiğinde buradan haber geliyor.
    const { data: listener } = supabase.auth.onAuthStateChange((event, next) => {
      if (cancelled) return;

      // TOKEN_REFRESHED yalnızca jetonu tazeliyor; profili yeniden çekmeye
      // gerek yok, her saatte bir gereksiz istek olurdu.
      if (event === "TOKEN_REFRESHED") {
        setSession(next);
        return;
      }

      if (event === "SIGNED_OUT") {
        queryClient.clear();
      }

      void applySession(next);
    });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, [applySession, queryClient]);

  const signIn = React.useCallback(
    async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        // Supabase hangi e-postanın kayıtlı olduğunu sızdırmamak için tek mesaj
        // döndürüyor; biz de aynı ayrımsızlığı koruyoruz.
        throw new Error(
          /invalid login credentials/i.test(error.message)
            ? "E-posta veya parola hatalı."
            : /email not confirmed/i.test(error.message)
              ? "Hesabın e-posta doğrulaması tamamlanmamış. Yöneticinize başvurun."
              : error.message,
        );
      }

      await applySession(data.session);

      // Son giriş zamanı ve denetim izi. Başarısız olması girişi engellememeli:
      // kullanıcı içeri girmiş durumda, kayıt tutulamaması ayrı bir sorun.
      try {
        await recordLogin();
      } catch (error) {
        console.error("Giriş kaydı yazılamadı:", error);
      }
    },
    [applySession],
  );

  const signOut = React.useCallback(async () => {
    await supabase.auth.signOut();
    queryClient.clear();
  }, [queryClient]);

  const refreshProfile = React.useCallback(async () => {
    if (!session) return;

    const loaded = await fetchProfile(session.user.id);
    setProfile(loaded);
    setStatus(loaded?.is_active ? "signedIn" : "inactive");
  }, [session]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      profile,
      isAdmin: profile?.is_admin === true,
      signIn,
      signOut,
      refreshProfile,
    }),
    [status, session, profile, signIn, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth, <AuthProvider> içinde kullanılmalıdır.");
  }

  return context;
}
